/**
 * Silence auto-stop for a recording left running (CLA-1564).
 *
 * A recording left running in an empty room — the laptop open over lunch, the
 * clinician gone home — keeps capturing until someone notices, and at worst
 * runs into the NEXT patient's consult. The 6-hour ceiling (CLA-969) bounds
 * the first case, not the second. This clock is the backstop: after a long,
 * CONTINUOUS stretch of silence it warns, and later finalises the recording
 * exactly as Stop would (never discards).
 *
 * The rules (decided by John, 2026-09-29):
 *  - silence is measured in ~100 ms RMS windows; ANY window above the floor
 *    resets the clock;
 *  - paused and capture-lost time never count, and the clock resets on resume;
 *  - 30 min of silence → warn ("Keep recording" re-arms, never disables);
 *  - 60 min of silence → finalise with `stopped_reason=silence_timeout`.
 *
 * The windows come from measured consults (CLA-1564 comment, 2026-09-29): the
 * longest in-consult quiet stretch across a week of AU prod was ~30 min (a
 * procedure wait); at a 60-minute window no genuine consult would have been
 * cut. Those measurements were on iOS audio; web needs its own floor — see
 * `SILENCE_FLOOR_DBFS`.
 *
 * Pure: no timers, no store, no Web Audio. The store feeds it one reading per
 * poll with the wall-clock time of the reading.
 */

/**
 * The silence floor for web, in dBFS, measured on the SAME AnalyserNode the
 * live level monitor uses — i.e. after the browser's own capture processing
 * (Chrome/Safari default to auto gain control + noise suppression on
 * getUserMedia audio, and the analyser sees the processed track).
 *
 * Why −50, and why that is the conservative choice:
 *  - Web recordings on prod sit ~23 dB LOUDER than iOS (AGC lifts speech), so
 *    the same absolute floor is ~23 dB harder to reach on web relative to the
 *    conversation. A window has to be far quieter, relative to the people in
 *    the room, to count.
 *  - Measured: none of the 25 web consults of the week had a silent stretch
 *    over 1.2 min at ANY floor down to −40 dBFS. −50 sits a further 10 dB
 *    below that, so a real web consult reaching 60 continuous minutes under it
 *    is not a realistic risk.
 *  - It is also the floor the server-side measurement used (`−50 RMS`), so the
 *    calibration telemetry below is comparable with those numbers.
 *  - The known cost: no web recording left running in an empty room has been
 *    measured yet. If a browser's processing leaves an empty room's noise
 *    above −50, this never fires on web. That is the acceptable failure —
 *    the 6-hour ceiling still bounds it, and failing to fire is safer than
 *    cutting a consult. `recording_stopped` telemetry carries the longest
 *    silent stretch at several floors plus the level percentiles, so the floor
 *    can be recalibrated from real web data rather than guessed again.
 */
export const SILENCE_FLOOR_DBFS = -50

export const SILENCE_WARN_MS = 30 * 60 * 1000
export const SILENCE_STOP_MS = 60 * 60 * 1000

/**
 * Samples per reading. 4096 is ~85 ms at 48 kHz (~93 ms at 44.1 kHz): the
 * "~100 ms RMS window" of the decision, and the largest power of two under it.
 */
export const SILENCE_WINDOW_SAMPLES = 4096
/** How often the store takes a reading. */
export const SILENCE_POLL_MS = 100

/** Floors whose longest silent stretch is reported for calibration only. */
export const CALIBRATION_FLOORS_DBFS = [-40, -45, -50, -55, -60, -70] as const

/**
 * RMS level of one window in dBFS, or null when the window cannot be judged.
 *
 * Exact digital zero is "unmeasurable", NOT "silent": it is what a suspended
 * AudioContext, a detached analyser or a muted track hands back, and none of
 * those says anything about the room. Treating it as silence would let a
 * broken meter finalise a consult that is in full swing. A real microphone in
 * a real room does not produce exact zeros; if some browser's noise
 * suppression turns out to, the clock simply never fires there, and the
 * `unmeasured_windows` telemetry shows it.
 */
export function rmsDbfs(samples: ArrayLike<number>): number | null {
  if (samples.length === 0) return null
  let sumSq = 0
  for (let i = 0; i < samples.length; i++) {
    const v = samples[i]
    if (!Number.isFinite(v)) return null
    sumSq += v * v
  }
  if (sumSq === 0) return null
  return 10 * Math.log10(sumSq / samples.length)
}

export type SilenceVerdict = 'none' | 'warn' | 'stop'

export interface SilenceStats {
  /** Longest continuous silent stretch at the active floor, in ms. */
  maxSilenceMs: number
  /** Same, per calibration floor, keyed by the floor (e.g. `-45`). */
  maxSilenceMsByFloor: Record<string, number>
  /** 10th / 50th percentile of window levels, dBFS (1 dB bins). Null with no readings. */
  levelP10Dbfs: number | null
  levelP50Dbfs: number | null
  windows: number
  unmeasuredWindows: number
}

const HIST_MIN_DBFS = -120

/** A run of silence below one floor: when it began, and the longest so far. */
interface Run {
  floor: number
  startedAt: number | null
  max: number
}

export class SilenceClock {
  private readonly floor: number
  private readonly warnMs: number
  private readonly stopMs: number
  /** The decision clock. Reset by resume, capture return and "Keep recording". */
  private silentSince: number | null = null
  private warned = false
  /**
   * Calibration runs. Reset by gaps in capture (pause, loss), but NOT by
   * "Keep recording" — the room stayed silent, and that is what they measure.
   */
  private readonly runs: Run[]
  private readonly hist = new Array<number>(-HIST_MIN_DBFS + 1).fill(0)
  private windows = 0
  private unmeasured = 0

  constructor(opts?: { floorDbfs?: number; warnMs?: number; stopMs?: number }) {
    this.floor = opts?.floorDbfs ?? SILENCE_FLOOR_DBFS
    this.warnMs = opts?.warnMs ?? SILENCE_WARN_MS
    this.stopMs = opts?.stopMs ?? SILENCE_STOP_MS
    const floors = new Set<number>([...CALIBRATION_FLOORS_DBFS, this.floor])
    this.runs = [...floors].map((floor) => ({ floor, startedAt: null, max: 0 }))
  }

  /**
   * Feed one reading taken at wall-clock `now` (ms). `level` is dBFS, or null
   * when the window could not be judged — which resets the clock like sound
   * does, because only a window positively below the floor is silence.
   *
   * Silence is measured from the first silent reading to the latest one, in
   * wall-clock time, so a throttled background tab (fewer readings) reaches
   * the same verdict at the same time — provided every reading it DID take
   * was silent. Any audible reading in between resets it.
   *
   * Returns `warn` once per silent run (again after `rearm`), and `stop` on
   * every reading from the stop threshold on; the caller makes that idempotent.
   */
  sample(now: number, level: number | null): SilenceVerdict {
    if (level === null) {
      this.unmeasured += 1
      this.silentSince = null
      this.warned = false
      for (const run of this.runs) run.startedAt = null
      return 'none'
    }

    this.windows += 1
    const bin = Math.min(0, Math.max(HIST_MIN_DBFS, Math.round(level)))
    this.hist[bin - HIST_MIN_DBFS] += 1

    for (const run of this.runs) {
      if (level < run.floor) {
        if (run.startedAt === null) run.startedAt = now
        run.max = Math.max(run.max, now - run.startedAt)
      } else {
        run.startedAt = null
      }
    }

    if (level >= this.floor) {
      this.silentSince = null
      this.warned = false
      return 'none'
    }
    if (this.silentSince === null) this.silentSince = now
    const silentFor = now - this.silentSince
    if (silentFor >= this.stopMs) return 'stop'
    if (silentFor >= this.warnMs && !this.warned) {
      this.warned = true
      return 'warn'
    }
    return 'none'
  }

  /** Is the decision clock inside a silent run right now? */
  get inSilence(): boolean {
    return this.silentSince !== null
  }

  /** How long the current silent run has lasted, as of the last reading. */
  silentMs(now: number): number {
    return this.silentSince === null ? 0 : Math.max(0, now - this.silentSince)
  }

  /**
   * "Keep recording": restart the decision clock and re-arm the warning. The
   * check stays on — it will warn again after another full warning window of
   * silence, and stop after another full stop window.
   */
  rearm(): void {
    this.silentSince = null
    this.warned = false
  }

  /**
   * Capture was interrupted (pause, mic lost) or has just resumed. Time on
   * either side of the gap must not be joined into one silent stretch, so the
   * decision clock AND the calibration runs restart.
   */
  interrupt(): void {
    this.rearm()
    for (const run of this.runs) run.startedAt = null
  }

  stats(): SilenceStats {
    const byFloor: Record<string, number> = {}
    for (const run of this.runs) byFloor[String(run.floor)] = run.max
    return {
      maxSilenceMs: byFloor[String(this.floor)] ?? 0,
      maxSilenceMsByFloor: byFloor,
      levelP10Dbfs: this.percentile(0.1),
      levelP50Dbfs: this.percentile(0.5),
      windows: this.windows,
      unmeasuredWindows: this.unmeasured,
    }
  }

  private percentile(p: number): number | null {
    if (this.windows === 0) return null
    const target = Math.max(1, Math.ceil(this.windows * p))
    let seen = 0
    for (let i = 0; i < this.hist.length; i++) {
      seen += this.hist[i]
      if (seen >= target) return i + HIST_MIN_DBFS
    }
    return 0
  }
}
